import { useEffect, useRef, useState } from "react";
import { LifeBuoy, X, Paperclip, Send, Loader2, CheckCircle2 } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { toast } from "sonner";
import { api } from "../lib/api";

/**
 * Floating "Report a problem" launcher + modal.
 *
 * Always-reachable from the bottom-right corner once a user is authenticated.
 * Submissions are stored server-side (and optionally emailed via Resend) by
 * POST /api/support/report.
 */
export default function ReportProblem() {
  const [open, setOpen] = useState(false);
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [screenshot, setScreenshot] = useState(null); // {name, dataUrl}
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const fileRef = useRef(null);

  // Reset state when modal closes (after a short delay so the success view shows)
  useEffect(() => {
    if (open) return;
    const t = setTimeout(() => {
      setSubject("");
      setDescription("");
      setScreenshot(null);
      setSubmitted(false);
    }, 250);
    return () => clearTimeout(t);
  }, [open]);

  // Esc to close
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const onPickFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please attach an image file");
      return;
    }
    if (file.size > 3 * 1024 * 1024) {
      toast.error("Screenshot must be under 3 MB");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setScreenshot({ name: file.name, dataUrl: reader.result });
    };
    reader.readAsDataURL(file);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (subject.trim().length < 2) {
      toast.error("Add a short subject");
      return;
    }
    if (description.trim().length < 2) {
      toast.error("Describe what went wrong");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/support/report", {
        subject: subject.trim(),
        description: description.trim(),
        page_path: typeof window !== "undefined" ? window.location.pathname : null,
        user_agent: typeof navigator !== "undefined" ? navigator.userAgent : null,
        screenshot_data_url: screenshot?.dataUrl || null,
      });
      setSubmitted(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send report");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        title="Report a problem"
        aria-label="Report a problem"
        className="fixed bottom-20 right-5 z-40 w-12 h-12 rounded-full bg-zinc-900 text-white shadow-lg hover:bg-zinc-800 transition-colors flex items-center justify-center ring-1 ring-white/10"
        data-testid="report-problem-launcher"
      >
        <LifeBuoy className="w-5 h-5" />
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[70] bg-zinc-950/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-3 sm:p-6"
          onClick={() => setOpen(false)}
          data-testid="report-problem-modal"
        >
          <div
            className="w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-100">
              <div className="flex items-center gap-2.5">
                <LifeBuoy className="w-4.5 h-4.5 text-zinc-900" />
                <p className="font-heading text-lg text-zinc-900">Report a problem</p>
              </div>
              <button
                onClick={() => setOpen(false)}
                aria-label="Close"
                className="text-zinc-400 hover:text-zinc-900"
                data-testid="report-problem-close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {submitted ? (
              <div className="px-6 py-10 text-center" data-testid="report-problem-success">
                <CheckCircle2 className="w-10 h-10 mx-auto text-emerald-600 mb-4" />
                <p className="font-heading text-xl text-zinc-900">Thanks — we got it.</p>
                <p className="text-sm text-zinc-600 mt-2">
                  Our team will read your report and follow up if we need more info.
                </p>
                <Button className="mt-6" onClick={() => setOpen(false)} data-testid="report-problem-success-close">
                  Close
                </Button>
              </div>
            ) : (
              <form onSubmit={submit} className="px-5 py-5 space-y-4">
                <div>
                  <label className="text-xs uppercase tracking-[0.18em] text-zinc-500 font-medium block mb-2">
                    Subject
                  </label>
                  <Input
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    placeholder="e.g. Translation didn't load"
                    maxLength={200}
                    data-testid="report-problem-subject"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.18em] text-zinc-500 font-medium block mb-2">
                    What happened?
                  </label>
                  <Textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Tell us what you were doing, what you expected, and what went wrong."
                    rows={5}
                    maxLength={5000}
                    data-testid="report-problem-description"
                  />
                  <p className="text-xs text-zinc-400 mt-1">{description.length}/5000</p>
                </div>
                <div>
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/*"
                    onChange={onPickFile}
                    className="hidden"
                    data-testid="report-problem-file-input"
                  />
                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => fileRef.current?.click()}
                      className="h-9 text-xs"
                      data-testid="report-problem-attach"
                    >
                      <Paperclip className="w-3.5 h-3.5 mr-1.5" />
                      {screenshot ? "Replace screenshot" : "Attach screenshot"}
                    </Button>
                    {screenshot && (
                      <div className="flex items-center gap-2 text-xs text-zinc-600" data-testid="report-problem-attached-name">
                        <span className="truncate max-w-[180px]">{screenshot.name}</span>
                        <button
                          type="button"
                          onClick={() => setScreenshot(null)}
                          className="text-zinc-400 hover:text-zinc-900"
                          aria-label="Remove screenshot"
                          data-testid="report-problem-remove-attachment"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center justify-end gap-2 pt-2">
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => setOpen(false)}
                    data-testid="report-problem-cancel"
                  >
                    Cancel
                  </Button>
                  <Button type="submit" disabled={submitting} data-testid="report-problem-submit">
                    {submitting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" /> Send report
                      </>
                    )}
                  </Button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}
